import React, { useState, useEffect, useRef } from 'react';
import { OrderSearchResult, searchOrders } from '../../services/complaintsAPI';
import styles from './OrderSelectionStep.module.css';
import useDebounce from '../../../../hooks/useDebounce';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { ComplaintWizardState } from '../NewComplaintModal';
import OrderCard from '../Shared/OrderCard/OrderCard';

import { formatDocument, formatDateSafely } from '../../utils/formatters';
import SearchIcon from '@mui/icons-material/Search';
import StoreIcon from '@mui/icons-material/Store';
import OrderDetailModal from '../../../orders/components/Pedidos/OrderDetailModal';
import { Order } from '../../../orders/services/ordersAPI';
import { SkBox, SkText } from '../../../../components/SkeletonLoader/SkeletonLoader';

interface OrderSelectionStepProps {
    data: ComplaintWizardState;
    updateData: (updates: Partial<ComplaintWizardState>) => void;
    goToStep?: (step: number) => void;
}

const OrderSelectionStep: React.FC<OrderSelectionStepProps> = ({ data, updateData, goToStep }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [results, setResults] = useState<OrderSearchResult[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Construct a dummy Order object for the modal
    const dummyOrder: Order = {
        ID: data.order?.id || 0,
        COD_TEOREMA: Number(data.order?.code || 0),
        cliente: data.order?.clientName || '',
        TIPO_PEDIDO: 'N/A',
        EMISSAO: data.order?.emissionDate || '',
        PREVISAO: '',
        SAIDA: null,
        NOTA_FISCAL: null,
        SITUACAO: 'Faturado',
    } as unknown as Order;

    const debouncedSearchTerm = useDebounce(searchTerm, 500);

    // Handle outside click for dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Search Effect
    useEffect(() => {
        const doSearch = async () => {
            if (debouncedSearchTerm.length < 2) {
                setResults([]);
                setShowDropdown(false);
                return;
            }
            setIsLoading(true);
            try {
                const rawData = await searchOrders(debouncedSearchTerm);
                const list = Array.isArray(rawData) ? rawData : (rawData as any)?.data ?? [];

                // Filtro rigoroso: Ignora itens sem código ou com #N/A literal
                const filtered = list.filter((res: OrderSearchResult) => {
                    const c = (res.code || '').toString().trim().toUpperCase();
                    return c !== '' && c !== 'N/A' && c !== '#N/A' && c !== '#NA' && c !== 'NA';
                });

                setResults(filtered);
                setShowDropdown(true);
                setActiveIndex(-1);
            } catch (err) {
                console.error(err);
            } finally {
                setIsLoading(false);
            }
        };

        if (!data.order) {
            doSearch();
        }
    }, [debouncedSearchTerm, data.order]);

    const handleSelectOrder = (order: OrderSearchResult) => {
        updateData({ order, items: [] }); // Reset items when order changes
        setSearchTerm('');
        setShowDropdown(false);
    };

    const handleRemoveOrder = () => {
        updateData({ order: null, items: [] });
        setSearchTerm('');
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!showDropdown || results.length === 0) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setActiveIndex(prev => (prev < results.length - 1 ? prev + 1 : prev));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setActiveIndex(prev => (prev > 0 ? prev - 1 : 0));
                break;
            case 'Enter':
                e.preventDefault();
                if (activeIndex >= 0 && activeIndex < results.length) {
                    handleSelectOrder(results[activeIndex]);
                }
                break;
            case 'Escape':
                setShowDropdown(false);
                break;
        }
    };

    const highlightMatch = (text: string | number | undefined, query: string) => {
        if (!text) return '';
        const stringText = text.toString();
        if (!query.trim()) return stringText;

        // Escapa caracteres especiais para o Regex
        const escapedQuery = query.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
        
        // Versão limpa da query (só números) para bater com IDs/CNPJs sem formatação
        const cleanQuery = query.replace(/\D/g, '');
        const escapedCleanQuery = cleanQuery.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');

        // Cria um regex que busca a query original OU a versão só com números
        const regexPattern = cleanQuery.length > 0 && cleanQuery !== query 
            ? `(${escapedQuery}|${escapedCleanQuery})` 
            : `(${escapedQuery})`;

        const parts = stringText.split(new RegExp(regexPattern, 'gi'));

        return (
            <>
                {parts.map((part, i) => {
                    const isMatch = part.toLowerCase() === query.toLowerCase() || 
                                   (cleanQuery.length > 0 && part.toLowerCase() === cleanQuery.toLowerCase());
                    return isMatch 
                        ? <span key={i} className={styles.highlight}>{part}</span> 
                        : part;
                })}
            </>
        );
    };

    const SearchSkeleton = () => (
        <>
            {[1, 2, 3].map((i) => (
                <div key={i} className={styles.resultItemSkeleton}>
                    <div className={styles.resultMain} style={{ marginBottom: '12px' }}>
                        <div className={styles.orderIdentifiers}>
                            <SkBox width={60} height={20} borderRadius={6} />
                            <SkBox width={40} height={14} />
                        </div>
                        <SkBox width={80} height={14} />
                    </div>
                    <div className={styles.resultSub}>
                        <SkBox width={16} height={16} borderRadius="50%" />
                        <SkText width="60%" height={16} />
                        <SkText width="20%" height={14} />
                    </div>
                </div>
            ))}
        </>
    );

    if (data.order) {
        return (
            <div className={styles.container}>
                <OrderCard
                    order={data.order}
                    onRemove={handleRemoveOrder}
                    onViewDetails={() => setIsOrderModalOpen(true)}
                />
                
                {isOrderModalOpen && (
                    <OrderDetailModal
                        order={dummyOrder}
                        onClose={() => setIsOrderModalOpen(false)}
                    />
                )}
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.searchContainer} ref={dropdownRef}>
                <label className={styles.label}>Buscar pedido</label>
                <div className={styles.searchWrapper}>
                    <SearchIcon className={styles.searchIcon} />
                    <input
                        type="text"
                        className={styles.searchInput}
                        placeholder="Ex: Número/código do pedido, Nome do Cliente ou CNPJ..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onFocus={() => { if (results.length > 0) setShowDropdown(true); }}
                        onKeyDown={handleKeyDown}
                        autoFocus
                    />
                </div>

                {showDropdown && (
                    <div className={styles.resultsDropdown}>
                        {results.length === 0 ? (
                            isLoading ? <SearchSkeleton /> : (
                                <div className={styles.resultItem}>Nenhum pedido encontrado.</div>
                            )
                        ) : (
                            <div className={isLoading ? styles.resultsLoadingPulse : ''}>
                                {results.map((res, index) => (
                                    <div
                                        key={res.id}
                                        className={`${styles.resultItem} ${index === activeIndex ? styles.active : ''}`}
                                        onClick={() => handleSelectOrder(res)}
                                    >
                                    <div className={styles.resultMain}>
                                        <div className={styles.orderIdentifiers}>
                                            <span className={styles.orderCodeBadge}>#{highlightMatch(res.code, searchTerm)}</span>
                                            <span className={styles.orderIdText}>ID: {highlightMatch(res.id, searchTerm)}</span>
                                        </div>
                                        <div className={styles.orderDate}>
                                            <CalendarTodayIcon fontSize="inherit" />
                                            <span>{formatDateSafely(res.emissionDate)}</span>
                                        </div>
                                    </div>
                                    <div className={styles.resultSub}>
                                        <StoreIcon fontSize="inherit" className={styles.storeIcon} />
                                        <span className={styles.clientName} title={res.clientName}>
                                            {highlightMatch(res.clientName, searchTerm)}
                                        </span>
                                        <span className={styles.clientDoc}>
                                            {highlightMatch(formatDocument(res.clientCnpj), searchTerm)}
                                        </span>
                                    </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default OrderSelectionStep;
